import { z } from "zod";
import { recipientPolicySchema } from "./notificationDomains";
import { ROLE_NAMES, SCOPE_TYPES } from "../permissions";
import { CREDENTIAL_TARGET_TYPES } from "./credentialTargets";
import {
  DBT_CAUSE_CODES,
  DBT_CLONE_REASONS,
  dbtFixFor,
  labelDbtCause,
  type DbtCauseCode,
  type DbtCloneReason,
} from "./dbtCauses";
import { WAREHOUSE_ERROR_HINTS, WAREHOUSE_ERROR_KINDS } from "../warehouse/warehouseErrorKinds";
import {
  EXTRACT_CAUSE_CODES,
  IMPORT_CAUSES,
  LOAD_CAUSE_CODES,
  PLATFORM_CAUSE_CODES,
  TEXT_CAUSE_CODES,
  importCauseFix,
  runLevelRetryWords,
  type ImportCauseEntry,
} from "./importCauses";
import { RUN_KINDS, STEP_NAMES, runIdSchema } from "./runs";
import { SECURITY_CATEGORIES, securityFactsSchema } from "./securityAlerts";
import { httpLinkSchema } from "./links";
import { supportUrlSchema } from "./authClientConfig";
import { monitorFactsSchema } from "./monitor";
import { OFFBOARDING_ITEM_KINDS } from "./tenantOffboarding";
import { CANARY_CODES, canaryCheckSchema } from "./canary";

/**
 * The notification module's shared contract (contract-notif §3 and §6): the closed lists, the
 * outbound host allow-list and its matcher, every sentence a delivery failure, a run's cause or
 * a subject's help can show, the test-status derivation, and the zod views and bodies of the
 * instance and platform routes.
 *
 * Pure and dependency-free beyond zod, so the server, the client and the runner all read the
 * same lists. This file names no environment variable (T-S7): the runner parses its own
 * configuration in `runner/notifications/config.ts`, and nothing here knows how the key is
 * found. Every sentence carries identifiers, counts and closed codes only (SEC-17): no address,
 * no URL, no variable name.
 */

// ---------------------------------------------------------------------------------------------
// Closed lists (§3.1)
// ---------------------------------------------------------------------------------------------

/**
 * "When": the outcome of a run. Warning is a partial import, a warned or failed dbt test, or a
 * column change first seen in the load (#1663, `toldSchemaChanges`).
 */
export const NOTIFY_OUTCOMES = ["failure", "warning", "success"] as const;
export type NotifyOutcome = (typeof NOTIFY_OUTCOMES)[number];

/**
 * "What": the owner's "level" (D1). `credentials` (platform migration `credential_checks`) is the
 * runner's daily credential check: a sign-in that was refused, expires soon, or works again.
 * `data_health` (platform migration `monitor`) is skyMonitor: stale, empty or unusual tables
 * and schedules that did not run.
 */
export const NOTIFY_SUBJECTS = [
  "import",
  "transformation",
  "schedule",
  "credentials",
  "data_health",
] as const;
export type NotifySubject = (typeof NOTIFY_SUBJECTS)[number];

/**
 * `access_invite` (platform migration `notification_access_invite`) is the invitation email a grant writes in its own
 * transaction: transactional mail to the granted address, never routed by a rule. `security`
 * (platform migration `security_alerts`) is an act the runner read from an audit chain, sent to
 * the admins who subscribed themselves (`shared/types/securityAlerts.ts`). `monitor` (platform
 * migration `monitor`) is a skyMonitor incident that opened, escalated, reminded or recovered
 * (`shared/types/monitor.ts`), routed by the Data health cells. `schedule` (platform migration
 * `notification_kind_schedule`, round 10 wave 2) is the due scan saying a schedule's firing
 * started skipping a connection another run holds, or that it imports that connection again,
 * routed by the Schedules warning cell and the schedule's watchers. `offboarding` (platform
 * migration `tenant_deleted`, F32 PR3) is a deleted tenant's offboarding list, written by the web
 * in the delete's transaction and sent to the L1 (ops) team only, never to a customer. `canary`
 * (platform migration `canary`, #826) is the daily canary's verdict when it failed, missed a day
 * or recovered, written by the runner's verdict loop and sent to the L1 team only.
 */
export const NOTIFY_EVENT_KINDS = [
  "run",
  "instance_test",
  "email_test",
  "access_invite",
  "credential",
  "security",
  "monitor",
  "schedule",
  "offboarding",
  "canary",
  // #2165 (platform migration `notification_kind_l1_teams_test`): a PlatformAdmin's "Send a
  // test" on the L1 Teams channel card, posted to that channel only.
  "l1_teams_test",
] as const;
export type NotifyEventKind = (typeof NOTIFY_EVENT_KINDS)[number];

/** One invitation per grant id: a retried request or a reactivation never sends twice. */
export function accessInviteDedupeKey(grantId: string): string {
  return `access_invite:${grantId}`;
}

/**
 * Round 7 ("Resend an invitation email"): the nth resend of a grant's invitation has a key of its
 * own, so it is a new event the drain sends, and a retried request (the same n) still sends once.
 */
export function accessInviteResendDedupeKey(grantId: string, count: number): string {
  return `${accessInviteDedupeKey(grantId)}:resend:${count}`;
}

/** The `dedupe_key` pattern every resend matches (SQL `LIKE`), for the per-person caps. */
export const ACCESS_INVITE_RESEND_KEY_LIKE = "access_invite:%:resend:%";

/**
 * Round 7: resends per person (the invitation's recipient), counted like the test sends: one a
 * minute and five a day. The grant's own first invitation is not counted.
 */
export const INVITE_RESEND_CAPS: ReadonlyArray<{ windowSeconds: number; limit: number }> = [
  { windowSeconds: 60, limit: 1 },
  { windowSeconds: 86_400, limit: 5 },
];
export const INVITE_RESEND_CAP_MESSAGE =
  "An invitation can be resent to one person once a minute and five times a day.";

export const NOTIFY_CHANNELS = ["email", "teams"] as const;
export type NotifyChannel = (typeof NOTIFY_CHANNELS)[number];

export const NOTIFY_TARGET_KINDS = ["address", "instance_teams", "l1_teams", "l1_address"] as const;
export type NotifyTargetKind = (typeof NOTIFY_TARGET_KINDS)[number];

export const EVENT_STATUSES = [
  "new",
  "expanded",
  "no_rule",
  "no_recipients",
  "suppressed",
] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

/**
 * `held`: the repeat window's record of a target it did not send to (R6); never claimed.
 * `parked` (#463, platform 0050): an email whose SendGrid key secret does not exist; never claimed
 * and never sent until an operator releases it to `pending` (`docs/deploy/notifications.md`).
 */
export const DELIVERY_STATUSES = [
  "pending",
  "sending",
  "sent",
  "dead",
  "skipped",
  "held",
  "parked",
] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export const DELIVERY_FAILURE_CODES = [
  "not_configured",
  "key_rejected",
  "sender_unverified",
  "webhook_rejected",
  "host_refused",
  "payload_rejected",
  "rate_limited",
  "upstream_error",
  "timeout",
  "network",
  "vault_unreadable",
  "internal",
  // F32: a customer alert of a disabled tenant, dropped and recorded (`runner/notifications/tenantDisabled.ts`).
  "tenant_disabled",
  // #463: the SendGrid key's secret does not exist in its vault; the email is parked, not retried.
  "key_missing",
  // #1111: a typed address outside the tenant's Allowed notification domains while it is on
  // (`runner/notifications/allowedDomains.ts`).
  "outside_allowed_domains",
  // #1678: an invitation whose grant was revoked after it was expanded
  // (`runner/notifications/inviteAccess.ts`).
  "grant_inactive",
  // #1691: an invitation whose person was deactivated after it was expanded, the grant still
  // active (`runner/notifications/inviteAccess.ts`).
  "user_disabled",
] as const;
export type DeliveryFailureCode = (typeof DELIVERY_FAILURE_CODES)[number];

/** The runner's check of the SendGrid key (R3), shown on the Email delivery card (§4.4). */
export const EMAIL_KEY_STATUSES = [
  "not_configured",
  "valid",
  "missing_scope",
  "rejected",
  "unreachable",
  "unreadable",
] as const;
export type EmailKeyStatus = (typeof EMAIL_KEY_STATUSES)[number];

/** What a test's status route answers (§3.6). */
export const TEST_STATUSES = ["waiting", "sent", "failed", "not_configured"] as const;
export type TestStatus = (typeof TEST_STATUSES)[number];

/** The repeat window in minutes (D4): 0 sends every alert, up to one day. */
export const REPEAT_WINDOW = { min: 0, max: 1440, default: 60 } as const;

/** Addresses per rule, and on the L1 list. The migration's CHECK holds the same number. */
export const MAX_RULE_EMAILS = 20;

/** The (subject, outcome) cells, subject-major: the order of every view and body (fifteen). */
export const RULE_CELLS: ReadonlyArray<{ subject: NotifySubject; outcome: NotifyOutcome }> =
  NOTIFY_SUBJECTS.flatMap((subject) => NOTIFY_OUTCOMES.map((outcome) => ({ subject, outcome })));

// ---------------------------------------------------------------------------------------------
// On-screen words for the cells (D1: "What" is the subject, "When" the outcome)
// ---------------------------------------------------------------------------------------------

export const NOTIFY_SUBJECT_LABELS = {
  import: "Imports",
  transformation: "Transformations",
  schedule: "Schedules",
  credentials: "Credentials",
  data_health: "Data health",
} as const satisfies Record<NotifySubject, string>;

/** The rule's legend under a subject section. */
export const NOTIFY_OUTCOME_RULE_LABELS = {
  failure: "When a run fails",
  warning: "When a run has warnings",
  success: "When a run succeeds",
} as const satisfies Record<NotifyOutcome, string>;

/** The test picker's "When" options. */
export const NOTIFY_OUTCOME_LABELS = {
  failure: "Failure",
  warning: "Warning",
  success: "Success",
} as const satisfies Record<NotifyOutcome, string>;

/** The Credentials subject's own words for its three rules (2026-09-28 cred-check design). */
export const CREDENTIAL_OUTCOME_RULE_LABELS = {
  failure: "When a credential is refused or expired",
  warning: "When a credential expires within 30 days",
  success: "When a credential works again",
} as const satisfies Record<NotifyOutcome, string>;

/** The Data health subject's own words (skyMonitor, spec section 10). */
export const DATA_HEALTH_OUTCOME_RULE_LABELS = {
  failure: "When a monitor fails",
  warning: "When a monitor warns",
  success: "When a monitor recovers",
} as const satisfies Record<NotifyOutcome, string>;

/** A rule's legend: the run words, or the Credentials or Data health subject's own. */
export function ruleOutcomeLabel(subject: NotifySubject, outcome: NotifyOutcome): string {
  if (subject === "credentials") return CREDENTIAL_OUTCOME_RULE_LABELS[outcome];
  if (subject === "data_health") return DATA_HEALTH_OUTCOME_RULE_LABELS[outcome];
  return NOTIFY_OUTCOME_RULE_LABELS[outcome];
}

/** "What: Imports, When a run fails": the cell's name in a test headline and an email footer. */
export function ruleCellLabel(subject: NotifySubject, outcome: NotifyOutcome): string {
  return `What: ${NOTIFY_SUBJECT_LABELS[subject]}, ${ruleOutcomeLabel(subject, outcome)}`;
}

// ---------------------------------------------------------------------------------------------
// Outbound hosts (R7): the one source for the runner's guard profiles and the egress view
// ---------------------------------------------------------------------------------------------

/**
 * `*.` matches one or more labels in front of the rest, at a label boundary only. Teams covers
 * the legacy connector webhooks, Logic Apps, and the Workflows host Microsoft is moving to.
 * SendGrid's host is a constant; its EU host is a backlog row.
 */
export const NOTIFY_HOSTS = {
  teams: ["*.webhook.office.com", "*.logic.azure.com", "*.environment.api.powerplatform.com"],
  sendgrid: ["api.sendgrid.com"],
} as const;
export type NotifyHostChannel = keyof typeof NOTIFY_HOSTS;

const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Whether an authority (`host[:port]`) is one the channel may post to. Case-insensitive; no port
 * or exactly `:443` (Teams Workflows URLs carry it), any other port refused; a `*.` entry
 * matches at a label boundary only (`x.webhook.office.com` yes, `evilwebhook.office.com` and
 * `webhook.office.com` no). Anything that is not a plain DNS name (userinfo, brackets, an empty
 * label) is refused.
 */
export function matchesNotifyHost(channel: NotifyHostChannel, authority: string): boolean {
  const lower = authority.toLowerCase();
  const match = /^([a-z0-9.-]+)(?::(\d+))?$/.exec(lower);
  if (!match) return false;
  const host = match[1] ?? "";
  const port = match[2];
  if (port !== undefined && port !== "443") return false;
  if (!host.split(".").every((label) => HOST_LABEL.test(label))) return false;
  const entries: readonly string[] = NOTIFY_HOSTS[channel];
  return entries.some((entry) => {
    if (!entry.startsWith("*.")) return host === entry;
    const suffix = entry.slice(1); // ".webhook.office.com"
    return host.length > suffix.length && host.endsWith(suffix);
  });
}

// ---------------------------------------------------------------------------------------------
// Delivery failure sentences (§3.1): Recent deliveries, the test result lines, the Email card
// ---------------------------------------------------------------------------------------------

/**
 * Per code and channel. `{service}` is "Teams" or "SendGrid" by channel, `{status}` the HTTP
 * status, `{vault}` the vault the key is read from. A sentence never carries an address, a URL
 * or a variable name.
 */
export const FAILURE_SENTENCES = {
  not_configured: {
    email: "Not sent: email is not set up for this deployment.",
    teams: "Not sent: no Teams webhook. Add one on the General tab of Settings.",
  },
  // #2158: the same fix as the platform test dialog's, with no internal names. Teams never ends
  // on this code; its column keeps the email's words.
  key_rejected: {
    email:
      "SendGrid refused the key. A platform admin replaces the secret in {vault} with a key that has Mail Send permission, and the new key is used within 15 minutes.",
    teams:
      "SendGrid refused the key. A platform admin replaces the secret in {vault} with a key that has Mail Send permission, and the new key is used within 15 minutes.",
  },
  sender_unverified: {
    email: "SendGrid refused the sender: it is not a verified sender for this key.",
    teams: "SendGrid refused the sender: it is not a verified sender for this key.",
  },
  webhook_rejected: {
    email:
      "Teams refused the webhook ({status}). Replace the webhook on the General tab of Settings.",
    teams:
      "Teams refused the webhook ({status}). Replace the webhook on the General tab of Settings.",
  },
  host_refused: {
    email: "The webhook address is not one this deployment can post to.",
    teams: "The webhook address is not one this deployment can post to.",
  },
  payload_rejected: {
    email: "{service} refused the message ({status}).",
    teams: "{service} refused the message ({status}).",
  },
  rate_limited: {
    email: "{service} kept answering that too many messages were sent.",
    teams: "{service} kept answering that too many messages were sent.",
  },
  upstream_error: {
    email: "{service} answered with an error ({status}) on every try.",
    teams: "{service} answered with an error ({status}) on every try.",
  },
  timeout: {
    email: "{service} did not answer in time on every try.",
    teams: "{service} did not answer in time on every try.",
  },
  network: {
    email: "{service} could not be reached from this deployment.",
    teams: "{service} could not be reached from this deployment.",
  },
  vault_unreadable: {
    email: "The SendGrid API key could not be read from {vault}.",
    teams: "The Teams webhook could not be read from Key Vault.",
  },
  internal: {
    email: "Sending stopped unexpectedly. If it happens again, contact Skypoint support.",
    teams: "Sending stopped unexpectedly. If it happens again, contact Skypoint support.",
  },
  tenant_disabled: {
    email: "Not sent: the tenant is disabled.",
    teams: "Not sent: the tenant is disabled.",
  },
  outside_allowed_domains: {
    email: "this address is outside the allowed domains for your organization.",
    teams: "this address is outside the allowed domains for your organization.",
  },
  grant_inactive: {
    email: "Not sent: the access this invitation was for was removed.",
    teams: "Not sent: the access this invitation was for was removed.",
  },
  user_disabled: {
    email: "Not sent: the person was deactivated.",
    teams: "Not sent: the person was deactivated.",
  },
  key_missing: {
    email:
      "the SendGrid API key does not exist in {vault}. The email is kept until a platform operator releases it.",
    teams:
      "the SendGrid API key does not exist in {vault}. The email is kept until a platform operator releases it.",
  },
} as const satisfies Record<DeliveryFailureCode, Record<NotifyChannel, string>>;

const SERVICE_BY_CHANNEL = { email: "SendGrid", teams: "Teams" } as const satisfies Record<
  NotifyChannel,
  string
>;

/**
 * The sentence for a failure code on a channel. Without a status the " ({status})" part is
 * dropped; without a vault name the sentence says "the platform vault".
 */
export function failureSentence(
  code: DeliveryFailureCode,
  channel: NotifyChannel,
  values: { status?: number | null; vault?: string | null } = {},
): string {
  let sentence: string = FAILURE_SENTENCES[code][channel];
  // Function replacements: a value holding `$&`, `$'` or `$$` is inserted as it is.
  sentence =
    values.status === undefined || values.status === null
      ? sentence.replace(" ({status})", "")
      : sentence.replace("{status}", () => String(values.status));
  const vault = values.vault ? values.vault : "the platform vault";
  return sentence
    .replace("{service}", () => SERVICE_BY_CHANNEL[channel])
    .replace("{vault}", () => vault);
}

// ---------------------------------------------------------------------------------------------
// Cause sentences (§3.2): "Cause: {sentence} ({code})"
// ---------------------------------------------------------------------------------------------

/**
 * The connector error categories (`ConnectorErrorCategory` in `runner/steps/sanitiseError.ts`).
 * `shared/` cannot import the runner, so the runner's classify test asserts the two are equal.
 *
 * Product gaps row 4: these nine are `TEXT_CAUSE_CODES`, the fallback tier of the import cause
 * catalogue (`importCauses.ts`). They keep their meaning here because the credential check stores
 * one of them (`credential_checks.cause_code`); an import's cause may be any code of its source's
 * list, which `notifyCauseSchema` below accepts.
 */
export const CONNECTOR_CAUSE_CODES = TEXT_CAUSE_CODES;
export type ConnectorCauseCode = (typeof CONNECTOR_CAUSE_CODES)[number];

export const CAUSE_SOURCES = ["connector", "load", "dbt", "platform"] as const;
export type CauseSource = (typeof CAUSE_SOURCES)[number];

function sentencesOf<C extends string>(
  table: Record<C, ImportCauseEntry>,
  codes: readonly C[],
): Record<C, string> {
  return Object.fromEntries(codes.map((code) => [code, table[code].sentence])) as Record<C, string>;
}

/** A failed extract, read from the step's closed `errorCategory`: the nine text categories. */
export const CONNECTOR_CAUSE_SENTENCES: Record<ConnectorCauseCode, string> = sentencesOf(
  IMPORT_CAUSES.connector,
  TEXT_CAUSE_CODES,
);

/**
 * #1097: a connection's credential check, in words (the Configuration tab's check line and the
 * credential email's Cause), for every connector, so no word names a field one connector lacks.
 * A refused check asks the source to sign in AND to open what the connection names, so two of the
 * nine read differently from an extract's. A refused `connection` is the source answering that
 * the host the connection names is wrong (UKG Pro HCM's INVALID_HOST; a check that got no answer
 * is `unreachable` and never reads this table). `missing_object` is something the connection
 * names (a SQL database, a Mouseflow website) that is not there or that the sign-in may not open
 * (the SQL hub's DATABASE_NOT_FOUND is "not found or access denied"), not a selected table. The
 * other seven read as an extract's.
 */
export const CONNECTION_CHECK_SENTENCES: Record<ConnectorCauseCode, string> = {
  ...CONNECTOR_CAUSE_SENTENCES,
  connection: "The source did not accept the host name on the connection",
  missing_object:
    "Something the connection names does not exist at the source, or its sign-in cannot open it",
};

/** A failed load, categorised from the already sanitised step error: the nine text categories. */
export const LOAD_CAUSE_SENTENCES: Record<ConnectorCauseCode, string> = sentencesOf(
  IMPORT_CAUSES.load,
  TEXT_CAUSE_CODES,
);

/** A failed transform, from the step's parsed dbt cause. A new dbt code fails `npm run check`. */
export const DBT_CAUSE_SENTENCES = {
  COLUMN_NOT_FOUND: "A model refers to a column that does not exist",
  RELATION_NOT_FOUND: "A model refers to a table or view that does not exist",
  SQL_SYNTAX_ERROR: "The warehouse did not accept a model's SQL",
  VALUE_CONVERSION_FAILED: "A value could not be converted in a model",
  STATEMENT_TIMEOUT: "A model's statement ran past the warehouse's time limit",
  WAREHOUSE_UNREACHABLE: "The warehouse could not be reached",
  NO_CURRENT_DATABASE: "The warehouse session had no database selected",
  WAREHOUSE_ERROR: "The warehouse returned an error",
  MODEL_COMPILATION_ERROR: "A model or the dbt project did not compile",
  DBT_RUNTIME_ERROR: "dbt stopped with a runtime error",
  UPSTREAM_FAILED: "A model was skipped because a model it depends on failed",
  UNCLASSIFIED: "The cause was not recognised. The run page has the details",
  TEST_UNIQUE_FAILED: "A uniqueness test found duplicates",
  TEST_NOT_NULL_FAILED: "A not-null test found empty values",
  TEST_RELATIONSHIPS_FAILED: "A relationships test found rows with no match",
  TEST_ACCEPTED_VALUES_FAILED: "An accepted-values test found unexpected values",
  TEST_SINGULAR_FAILED: "A singular test returned rows",
  TEST_GENERIC_FAILED: "A generic test failed",
  REF_NOT_FOUND: "A model refers to another model that does not exist",
  SOURCE_NOT_FOUND: "A model refers to a source that is not defined",
  PROJECT_PARSE_ERROR: "The dbt project could not be read",
  DUPLICATE_RESOURCE: "The dbt project defines the same resource twice",
  SELECTOR_NOT_VALID: "A dbt job has a selector dbt cannot use",
  MODEL_TARGETS_LANDED_TABLE: "A dbt model would overwrite a loaded table",
  PHASE_CONFIG: "The transformation's settings are incomplete",
  PHASE_AUTH: "The warehouse or the repository refused the sign-in",
  PHASE_CLONE: "The dbt repository could not be cloned",
  PHASE_DEPS: "The dbt packages could not be installed",
  PHASE_COMPILE: "The dbt project did not compile",
  PHASE_RUN: "dbt run failed",
  PHASE_TEST: "dbt test failed",
  PHASE_INFRA: "The transformation job could not start",
  PHASE_TIMEOUT: "The transformation ran past its time limit",
  PHASE_INTERNAL: "The transformation stopped unexpectedly",
  PHASE_STALE: "A source table was past its freshness limit, so dbt did not run",
} as const satisfies Record<DbtCauseCode, string>;

/**
 * #1674: a failed dbt checkout by its reason, in the table's style (no full stop); an unknown
 * reason reads `PHASE_CLONE`'s own sentence. No branch: a message carries no identifier.
 */
export const DBT_CLONE_REASON_SENTENCES = {
  ref_not_found: "The dbt branch was not found in the repository",
  repo_unreadable: "The dbt repository was not found or the token cannot read it",
  auth: "The dbt repository refused the access token",
} as const satisfies Partial<Record<DbtCloneReason, string>>;

/**
 * The cause a notification carries: a source and a closed code, never an identifier. A dbt cause
 * may add `cloneReason` (#1674), a closed word on `PHASE_CLONE`.
 */
export const notifyCauseSchema = z.discriminatedUnion("source", [
  z.object({ source: z.literal("connector"), code: z.enum(EXTRACT_CAUSE_CODES) }).strict(),
  z.object({ source: z.literal("load"), code: z.enum(LOAD_CAUSE_CODES) }).strict(),
  z
    .object({
      source: z.literal("dbt"),
      code: z.enum(DBT_CAUSE_CODES),
      cloneReason: z.enum(DBT_CLONE_REASONS).optional(),
      // #1662: a WAREHOUSE_ERROR's closed hint kind (`dbtWarehouseKind`), never text.
      warehouseKind: z.enum(WAREHOUSE_ERROR_KINDS).optional(),
    })
    .strict()
    // PR #1694 review: the kind belongs to WAREHOUSE_ERROR only (`dbtWarehouseKind`), so a kind
    // on another code is refused rather than printed beside the wrong cause.
    .refine((cause) => cause.warehouseKind === undefined || cause.code === "WAREHOUSE_ERROR", {
      path: ["warehouseKind"],
      message: "warehouseKind is for WAREHOUSE_ERROR only",
    }),
  z.object({ source: z.literal("platform"), code: z.enum(PLATFORM_CAUSE_CODES) }).strict(),
]);
export type NotifyCause = z.infer<typeof notifyCauseSchema>;

/**
 * The cause tables by source (contract §3.2's `CAUSE_SENTENCES`). The import sources cover their
 * whole list (product gaps row 4); the nine read as `CONNECTOR_CAUSE_SENTENCES` and
 * `LOAD_CAUSE_SENTENCES` do, because both come from `IMPORT_CAUSES`.
 */
export const CAUSE_SENTENCES = {
  connector: sentencesOf(IMPORT_CAUSES.connector, EXTRACT_CAUSE_CODES),
  load: sentencesOf(IMPORT_CAUSES.load, LOAD_CAUSE_CODES),
  dbt: DBT_CAUSE_SENTENCES,
  platform: sentencesOf(IMPORT_CAUSES.platform, PLATFORM_CAUSE_CODES),
} as const satisfies Record<CauseSource, Record<string, string>>;

/** The cause's sentence, without the code. */
export function causeSentence(cause: NotifyCause): string {
  switch (cause.source) {
    case "connector":
      return CAUSE_SENTENCES.connector[cause.code];
    case "load":
      return CAUSE_SENTENCES.load[cause.code];
    case "dbt":
      if (cause.warehouseKind && cause.code === "WAREHOUSE_ERROR") {
        return `${CAUSE_SENTENCES.dbt[cause.code]}: ${WAREHOUSE_ERROR_HINTS[cause.warehouseKind].words}`;
      }
      return dbtCloneSentence(cause) ?? CAUSE_SENTENCES.dbt[cause.code];
    case "platform":
      return CAUSE_SENTENCES.platform[cause.code];
  }
}

function dbtCloneSentence(cause: {
  code: DbtCauseCode;
  cloneReason?: DbtCloneReason;
}): string | null {
  if (cause.code !== "PHASE_CLONE" || !cause.cloneReason) return null;
  const sentences: Partial<Record<DbtCloneReason, string>> = DBT_CLONE_REASON_SENTENCES;
  return sentences[cause.cloneReason] ?? null;
}

/** #1420: the cause's short label, the run page's badge, so a message never prints a raw code. */
export function causeLabel(cause: NotifyCause): string {
  if (cause.source === "dbt") return labelDbtCause(cause.code, cause.cloneReason);
  const table: Record<string, { label: string }> = IMPORT_CAUSES[cause.source];
  return table[cause.code]?.label ?? causeSentence(cause);
}

/**
 * The next step a notification prints after the cause (design §4.8): the catalogue's fix, static
 * text, so SEC-17 holds. A firewall fix names the run page, because the runner never reads the
 * platform's outbound address (only the web does). A dbt cause reads `dbtFixFor`, the run page's own fix (#1430), null for a code with none.
 */
export function causeFix(cause: NotifyCause, runLevelRetry = false): string | null {
  if (cause.source === "dbt") return dbtFixFor(cause.code, cause);
  const fix = importCauseFix(cause, null);
  return runLevelRetry ? runLevelRetryWords(fix) : fix;
}

// ---------------------------------------------------------------------------------------------
// Subject help (§3.7)
// ---------------------------------------------------------------------------------------------

/** Under each "What: …" section, so nobody is misled about where a transformation counts. */
export const SUBJECT_HELP = {
  import:
    "Imports a person starts with Import now or Import selected, including the dbt jobs those imports run.",
  transformation:
    "Transformation runs a person starts on their own. A dbt job inside an import or a schedule counts under Imports or Schedules.",
  schedule: "Every run a schedule starts, including its imports and its dbt jobs.",
  credentials:
    "Connection and destination sign-ins that were refused, expire soon, or work again. The runner checks them once a day.",
  data_health:
    "Stale, empty or unusual tables and schedules that did not run, found by skyMonitor.",
} as const satisfies Record<NotifySubject, string>;

/** Under every subject's "When a run succeeds" rule. */
export const SUCCESS_RULE_HELP =
  "Includes a run that succeeds after the previous run of the same schedule or connection failed. The message says Recovered.";

/** #1663: under the "When a run has warnings" rule of Imports and Schedules. */
export const WARNING_RULE_HELP =
  "Includes a run where a table's columns changed. Each change is told once, on the run that found it.";

/**
 * The 409 `STALE_UPDATE` sentences (§4.5). The server puts them in the error envelope and the
 * screens show the same words, so both read them from here.
 */
export const RULES_STALE_MESSAGE = "Someone else changed these rules. Reload to see their version.";
export const L1_STALE_MESSAGE = "Someone else changed the L1 recipients. Reload to see them.";

// ---------------------------------------------------------------------------------------------
// Test status derivation (§3.6)
// ---------------------------------------------------------------------------------------------

export interface TestDeliveryState {
  status: DeliveryStatus;
  failureCode: DeliveryFailureCode | null;
}

export interface DerivedTestStatus {
  status: TestStatus;
  /** Set only when the test failed on a delivery that has a code. */
  failureCode: DeliveryFailureCode | null;
}

/**
 * What a test's status route answers, from the event's status and its deliveries in creation
 * order. Pure; used by both status routes. In order:
 * 1. the event is `new`, or a delivery is `pending` or `sending`: `waiting`;
 * 2. no delivery (`no_rule`, `no_recipients`; the routes' 409 makes this defensive): `failed`;
 * 3. every delivery `skipped` with `not_configured`: `not_configured`;
 * 4. at least one `sent` and every delivery that is not `skipped` is `sent`: `sent` (a skipped
 *    target is named in the sentence the screen writes);
 * 5. otherwise `failed`, with the first `dead` delivery's code (or, with none dead, the first
 *    code any delivery carries).
 */
export function deriveTestStatus(
  eventStatus: EventStatus,
  deliveries: ReadonlyArray<TestDeliveryState>,
): DerivedTestStatus {
  if (
    eventStatus === "new" ||
    deliveries.some((d) => d.status === "pending" || d.status === "sending")
  ) {
    return { status: "waiting", failureCode: null };
  }
  if (deliveries.length === 0) return { status: "failed", failureCode: null };
  if (deliveries.every((d) => d.status === "skipped" && d.failureCode === "not_configured")) {
    return { status: "not_configured", failureCode: null };
  }
  const attempted = deliveries.filter((d) => d.status !== "skipped");
  if (attempted.length > 0 && attempted.every((d) => d.status === "sent")) {
    return { status: "sent", failureCode: null };
  }
  const dead = deliveries.find((d) => d.status === "dead");
  const coded = dead ?? deliveries.find((d) => d.failureCode !== null);
  return { status: "failed", failureCode: coded?.failureCode ?? null };
}

// ---------------------------------------------------------------------------------------------
// Facts (§3.3): what one event carries, and all a renderer may read
// ---------------------------------------------------------------------------------------------

/** Names are truncated to this many characters by the builder. */
export const FACT_NAME_MAX = 80;
/** Connection names listed by name; the rest are counted in `connectionsMore`. */
export const FACT_CONNECTION_NAMES_MAX = 10;
/** #1420: the dbt jobs a run's facts list by name. */
export const FACT_DBT_JOBS_MAX = 10;

const factName = z.string().max(FACT_NAME_MAX);
const count = z.number().int().nonnegative();
const utcTime = z.iso.datetime();

/**
 * #1422 (owner 2026-10-17, superseding D5 for this case): a cancelled run's event. Who cancelled
 * it (`by`: a person, or `system` for an automatic cancel; absent when it could not be read),
 * the person's display label (masked as "Started by" is), when, and the run's connections by id
 * and name, so a Watch or a rule for chosen connections on a connection whose steps were all
 * cancelled still finds it, each with its own outcome when it had finished before the cancel.
 * Ids and names only (SEC-17).
 */
export const CANCELLED_CONNECTIONS_MAX = 200;
export const cancelledFactsSchema = z
  .object({
    at: utcTime,
    by: z.enum(["person", "system"]).optional(),
    byLabel: factName.optional(),
    connections: z
      .array(
        z
          .object({
            id: z.uuid(),
            name: factName,
            /** Its own result when its steps had all ended before the cancel; absent: cancelled. */
            outcome: z.enum(NOTIFY_OUTCOMES).optional(),
          })
          .strict(),
      )
      .max(CANCELLED_CONNECTIONS_MAX),
  })
  .strict();
export type CancelledFacts = z.infer<typeof cancelledFactsSchema>;

/**
 * What an `access_invite` says (SEC-17): the grant's id, its scope and role, the method the
 * person signs in with, and who granted it. Never a secret, and never the recipient's address,
 * which is the event's `test_recipient` column.
 */
export const inviteFactsSchema = z
  .object({
    grantId: z.uuid(),
    scopeKind: z.enum(SCOPE_TYPES),
    /** The instance's or the tenant's name; "All tenants" at platform scope. */
    scopeName: factName,
    role: z.enum(ROLE_NAMES),
    /** The sign-in button the grant is pinned to ("Sign in with Microsoft"), or "email and password". */
    providerLabel: factName,
    /** The granting administrator's display name, else their email. */
    grantedByLabel: factName,
    /**
     * #1639: the deployment's "Contact Support" address (`AUTH_CLIENT` `supportUrl`, a `mailto:`
     * or `https:` URL), the one the sign-in page links. Absent when the deployment names none: the
     * email then links the sign-in page's default (`DEFAULT_SUPPORT_URL`).
     */
    supportUrl: supportUrlSchema.optional(),
  })
  .strict();
export type InviteFacts = z.infer<typeof inviteFactsSchema>;

/**
 * What a credential check found, as the event names it (2026-09-28 cred-check design):
 * `rejected` the first refusal, `reminder` every 7 days while it stays refused, `expired` a date
 * that has passed, `expiring` 30 or 7 days left, and `works_again` accepted after a failure. A
 * check that got no answer is never an event (owner answer b2): it is counted in telemetry only.
 */
export const CREDENTIAL_TRANSITIONS = [
  "rejected",
  "reminder",
  "expired",
  "expiring",
  "works_again",
] as const;
export type CredentialTransition = (typeof CREDENTIAL_TRANSITIONS)[number];

/** The outcome, and so the rule cell, of each transition. */
export const CREDENTIAL_TRANSITION_OUTCOME = {
  rejected: "failure",
  reminder: "failure",
  expired: "failure",
  expiring: "warning",
  works_again: "success",
} as const satisfies Record<CredentialTransition, NotifyOutcome>;

/**
 * What a `credential` event says (SEC-17): which target, what happened, the closed cause code,
 * the expiry day and the days left, and the field's LABEL from the connector definition. Never a
 * value, never a secret name, never the hub's message.
 */
export const credentialFactsSchema = z
  .object({
    targetType: z.enum(CREDENTIAL_TARGET_TYPES),
    /** The connection's name, or the destination section's words ("Stage storage"). */
    targetName: factName,
    /** A connection's connector (#1097: a SQL connector's refusal names more fields). */
    connectorName: factName.optional(),
    /** The field's label ("Client secret"), when the event is about one field's date. */
    fieldLabel: factName.optional(),
    transition: z.enum(CREDENTIAL_TRANSITIONS),
    causeCode: z.enum(CONNECTOR_CAUSE_CODES).optional(),
    expiresAt: utcTime.optional(),
    daysLeft: z.number().int().optional(),
    failingSince: utcTime.optional(),
  })
  .strict();
export type CredentialFacts = z.infer<typeof credentialFactsSchema>;

/**
 * Round 10 wave 2 (A1, design §1.1): a schedule's "connection busy" episode. `skipped` is the
 * first firing that left a connection out because another run held it; `running_again` the first
 * firing that imported it again. One event per firing per transition.
 */
export const SCHEDULE_SKIP_TRANSITIONS = ["skipped", "running_again"] as const;
export type ScheduleSkipTransition = (typeof SCHEDULE_SKIP_TRANSITIONS)[number];

/**
 * What a `schedule` event says (SEC-17): the transition, who held the connections (another
 * schedule, by name, or an import started by hand), how many firings skipped them and since when.
 * The schedule's and the connections' names are the facts' own `scheduleName` and
 * `connectionNames`. Names and counts only.
 */
export const scheduleSkipFactsSchema = z
  .object({
    transition: z.enum(SCHEDULE_SKIP_TRANSITIONS),
    /** `several`: the connections were held by more than one run's owner. */
    holderKind: z.enum(["schedule", "manual", "several"]),
    holderScheduleName: factName.optional(),
    skippedCount: count,
    since: utcTime,
    /** The firing still made a run of its other connections (and its transformations). */
    importedOthers: z.boolean(),
  })
  .strict();
export type ScheduleSkipFacts = z.infer<typeof scheduleSkipFactsSchema>;

/** How many of the list's items an `offboarding` event carries; the rest are counted. */
export const OFFBOARDING_FACT_ITEMS_MAX = 40;

/**
 * What an `offboarding` event says (F32 PR3): the deleted tenant, and the Azure resources and
 * databases Infra removes, as ids and names. Never a secret name (SEC-17): those are counted here
 * and listed on the tenant's offboarding list in skyData and in its `tenant.delete` audit row.
 */
export const offboardingFactsSchema = z
  .object({
    tenantId: z.uuid(),
    /** The tenant's Name (fixed), beside the facts' `tenantName`, which is its Display name. */
    name: z.string().max(63),
    resourcesMode: z.enum(["shared", "per_tenant", "byo_vault"]),
    deletedAt: utcTime,
    items: z
      .array(
        z
          .object({
            kind: z.enum(OFFBOARDING_ITEM_KINDS),
            name: z.string().min(1).max(512),
            id: z.string().max(512).optional(),
            /** The item may hold PHI (`shared/types/tenantOffboarding.ts`). */
            mayHoldPhi: z.literal(true).optional(),
          })
          .strict(),
      )
      .max(OFFBOARDING_FACT_ITEMS_MAX),
    /** Items past the cap, secret names excluded. */
    itemsMore: count,
    /** Secret names on the list (names only there; never here). */
    secretNames: count,
  })
  .strict();
export type OffboardingFacts = z.infer<typeof offboardingFactsSchema>;

/** How many failed checks a `canary` event carries. */
export const CANARY_FACT_CHECKS_MAX = 20;

/**
 * What a `canary` event says (#826, R4.7): the canary tenant, the transition (it failed, missed
 * its day, or is green again), the verdict's closed code, the run and which checks failed. Ids,
 * closed codes, numbers and the entity names skyData stores; never a warehouse name or text.
 */
export const canaryFactsSchema = z
  .object({
    tenantId: z.uuid(),
    transition: z.enum(["failed", "missed", "recovered"]),
    code: z.enum(CANARY_CODES),
    runId: runIdSchema.nullable(),
    /** The UTC day the verdict is for, `YYYY-MM-DD`. */
    day: z.iso.date(),
    checks: z.array(canaryCheckSchema).max(CANARY_FACT_CHECKS_MAX),
  })
  .strict();
export type CanaryFacts = z.infer<typeof canaryFactsSchema>;

/** One offboarding event per tenant: a retried delete never writes two. */
export function offboardingDedupeKey(tenantId: string): string {
  return `offboarding:${tenantId}`;
}

/** `schedule:<instanceId>:<scheduleId>:<scheduledForISO>:<transition>` (design §1.1). */
export function scheduleSkipDedupeKey(
  instanceId: string,
  scheduleId: string,
  scheduledFor: string,
  transition: ScheduleSkipTransition,
): string {
  return `schedule:${instanceId}:${scheduleId}:${scheduledFor}:${transition}`;
}

/** `providerLabel` for the Email form (External ID with a password; docs/auth/sso.md). */
export const INVITE_EMAIL_METHOD = "email and password";

/** `providerLabel` for a pin the web app offers no button for: never a guessed method. */
export const INVITE_OTHER_METHOD = "the sign-in method your administrator set up for you";

/**
 * Identifiers, names, counts, durations, closed enums, UTC times and one link (SEC-17). Never a
 * step or entity error, a table name, event payload text, a dbt message or node name, SQL, or a
 * cause's identifiers. `.strict()`: an unknown key is a builder bug, and the runner treats a
 * facts object that does not parse as a deterministic failure (R1).
 *
 * A `run` event has its outcome, subject, tenant, instance and run; an `instance_test` its
 * tenant, instance and the tested cell; an `email_test` names no tenant and no instance; an
 * `access_invite` its `invite` block and no instance id.
 */
const notificationFactsObject = z
  .object({
    eventKind: z.enum(NOTIFY_EVENT_KINDS),
    outcome: z.enum(NOTIFY_OUTCOMES).nullable(),
    recovered: z.boolean(),
    subject: z.enum(NOTIFY_SUBJECTS).nullable(),
    deploymentLabel: factName,
    tenantName: factName.nullable(),
    instanceName: factName.nullable(),
    instanceId: z.uuid().nullable(),
    runId: runIdSchema.optional(),
    runKind: z.enum(RUN_KINDS).optional(),
    scheduleName: factName.optional(),
    /**
     * #1268: what a transformation-only run ran, for its headline and held-back sentence: the one
     * dbt job's name, "N dbt jobs" or "Chosen models". Absent for any other run, and
     * for a step that recorded no job or whose job is gone. A name, cut to the facts limit.
     */
    transformationJobs: factName.optional(),
    /**
     * #1380: what kind of run `transformationJobs` names, so the held-back sentence never
     * compares the label: `chosen` (a pinned selection), `job` (one dbt job) or `jobs` (several).
     * A dbt job literally named "Chosen models" is `job`. Absent when `transformationJobs` is.
     */
    transformationKind: z.enum(["job", "jobs", "chosen"]).optional(),
    /**
     * #1420: the dbt jobs the run's transform step ran, each with its outcome (`failed`,
     * `succeeded`, `not_run` or `unknown`), so a reader of a failure knows whose job to open. Job names
     * only, never a model name or dbt text. At most FACT_DBT_JOBS_MAX, `dbtJobsMore` the rest.
     */
    dbtJobs: z
      .array(
        z
          .object({
            name: factName,
            outcome: z.enum(["failed", "succeeded", "not_run", "unknown"]),
          })
          .strict(),
      )
      .max(FACT_DBT_JOBS_MAX)
      .optional(),
    dbtJobsMore: count.optional(),
    connectionNames: z.array(factName).max(FACT_CONNECTION_NAMES_MAX),
    connectionsMore: count,
    counts: z
      .object({
        connections: count,
        connectionsFailed: count,
        tablesLoaded: count,
        tablesFailed: count,
        tablesSkipped: count,
        rowsLoaded: count,
        /**
         * #2392: of the tables counted in `tablesLoaded`, those the source sent no row for
         * (`noRowsTableCount`; a cancelled run's `tablesLoaded` adds the skipped ones); the
         * message counts them apart ("12 loaded, 8 had no rows").
         * Absent when none.
         */
        tablesNoRows: count.optional(),
        /**
         * #1422: a cancelled run only. What the extract reported, beside the loaded counts
         * above, which then come from the load step, so a run cancelled during the load never
         * reads its extracted tables and rows as loaded.
         */
        tablesExtracted: count.optional(),
        rowsExtracted: count.optional(),
        /** Product gaps row 2: the tables a scoped run covered (a count; never the names). */
        tablesInScope: count.optional(),
        modelsBuilt: count.optional(),
        modelsFailed: count.optional(),
        testsPassed: count.optional(),
        testsFailed: count.optional(),
        testsWarned: count.optional(),
        /**
         * Product gaps row 7: columns first seen in this run, added, held for review, or widened,
         * and on how many tables. Counts only: table and column names stay in the product.
         */
        columnsAdded: count.optional(),
        columnsWaiting: count.optional(),
        columnsWidened: count.optional(),
        /**
         * #1663: a column the table has and the source does not send, kept and not loaded, and a
         * type change only the source's connector saw. Counts only. An older drain refuses an
         * event carrying them (`facts_invalid`): lost, never sent wrongly (rollback note).
         */
        columnsNotSent: count.optional(),
        columnsRetyped: count.optional(),
        schemaTables: count.optional(),
      })
      .strict(),
    failedStep: z.enum(STEP_NAMES).optional(),
    stepIndex: z.number().int().positive().optional(),
    stepTotal: z.number().int().positive().optional(),
    cause: notifyCauseSchema.optional(),
    /** #1465: table runs were off when the run finished, so a retry is the run's. */
    runLevelRetry: z.literal(true).optional(),
    startedByLabel: factName.optional(),
    startedAt: utcTime.optional(),
    finishedAt: utcTime.optional(),
    durationSeconds: count.optional(),
    destinationLabel: factName.optional(),
    heldBackCount: count.optional(),
    heldBackSince: utcTime.optional(),
    /**
     * #817: set on a `run` event whose run finished `partially_succeeded`, so it reads "Partly
     * failed" as the Runs screen does (a `warning` outcome alone is also a run that succeeded
     * with a dbt test warning or a schema change). `transformFailed`: its transform step failed.
     */
    partlyFailed: z.object({ transformFailed: z.boolean() }).strict().optional(),
    /**
     * #1422: set on a `run` event whose run was cancelled, which has no outcome: it reaches only
     * the subscribers who chose every result (`runner/notifications/expand.ts`).
     */
    cancelled: cancelledFactsSchema.optional(),
    /**
     * #842: on a failed (or partly failed) scheduled run's event, the schedule's skyMonitor
     * `failed_run` alert that is still open: when it opened and a link to skyMonitor's Incidents
     * tab. Added at expansion from skyMonitor's own outbox event, never from a skyMonitor table
     * (`runner/notifications/openAlert.ts`); absent when skyMonitor is off or has no open alert.
     */
    openAlert: z.object({ openedAt: utcTime, link: httpLinkSchema.optional() }).strict().optional(),
    testSubject: z.enum(NOTIFY_SUBJECTS).optional(),
    testOutcome: z.enum(NOTIFY_OUTCOMES).optional(),
    /** Set on an `access_invite` only. */
    invite: inviteFactsSchema.optional(),
    /** Set on a `credential` event only. */
    credential: credentialFactsSchema.optional(),
    /** Set on a `security` event only. */
    security: securityFactsSchema.optional(),
    /** Set on a `monitor` event only: counts and numbers, never a table name (SEC-17). */
    monitor: monitorFactsSchema.optional(),
    /** Set on a `schedule` event only: names and counts (SEC-17). */
    scheduleSkip: scheduleSkipFactsSchema.optional(),
    /** Set on an `offboarding` event only: resource ids and names, no secret name. */
    offboarding: offboardingFactsSchema.optional(),
    /** Set on a `canary` event only: the verdict's code, ids and numbers (#826). */
    canary: canaryFactsSchema.optional(),
    /**
     * http(s) only, with no user or password in it: the link becomes a Teams Action.OpenUrl and
     * an email anchor.
     */
    link: httpLinkSchema.optional(),
  })
  .strict();

/** One facts object's rules: what its kind carries, and what only another kind may carry. */
function checkNotificationFacts(
  facts: z.infer<typeof notificationFactsObject>,
  ctx: z.RefinementCtx,
): void {
  const need: FactRule = (ok, path, message) => {
    if (!ok) ctx.addIssue({ code: "custom", path: [path], message });
  };
  checkKindFacts(facts, need);
  checkOtherKindsFacts(facts, need);
}

/** Records an issue at `path` unless `ok`. */
type FactRule = (ok: boolean, path: string, message: string) => void;

/** What each event kind carries. */
function checkKindFacts(facts: z.infer<typeof notificationFactsObject>, need: FactRule): void {
  const hasInstance =
    facts.tenantName !== null && facts.instanceName !== null && facts.instanceId !== null;
  switch (facts.eventKind) {
    case "run":
      // #1422: a cancelled run has no outcome, and only a cancelled run has none.
      need(
        (facts.outcome === null) === (facts.cancelled !== undefined),
        "outcome",
        "A run event has an outcome, unless it was cancelled.",
      );
      need(facts.subject !== null, "subject", "A run event has a subject.");
      need(facts.subject !== "credentials", "subject", "A run is never a credential event.");
      need(facts.subject !== "data_health", "subject", "A run is never a skyMonitor event.");
      need(facts.runId !== undefined, "runId", "A run event has a run id.");
      need(hasInstance, "instanceId", "A run event names its tenant and instance.");
      return;
    case "credential":
      need(facts.outcome !== null, "outcome", "A credential event has an outcome.");
      need(facts.subject === "credentials", "subject", "A credential event is a Credentials one.");
      need(facts.credential !== undefined, "credential", "A credential event names its target.");
      need(hasInstance, "instanceId", "A credential event names its tenant and instance.");
      return;
    case "monitor":
      need(facts.outcome !== null, "outcome", "A skyMonitor event has an outcome.");
      need(facts.subject === "data_health", "subject", "A skyMonitor event is a Data health one.");
      need(facts.monitor !== undefined, "monitor", "A skyMonitor event names its monitor.");
      need(hasInstance, "instanceId", "A skyMonitor event names its tenant and instance.");
      return;
    case "schedule":
      need(facts.outcome === "warning", "outcome", "A schedule event is a warning.");
      need(facts.subject === "schedule", "subject", "A schedule event is a Schedules one.");
      need(facts.scheduleSkip !== undefined, "scheduleSkip", "A schedule event names its skip.");
      need(facts.scheduleName !== undefined, "scheduleName", "A schedule event names it.");
      need(hasInstance, "instanceId", "A schedule event names its tenant and instance.");
      need(
        facts.recovered === (facts.scheduleSkip?.transition === "running_again"),
        "recovered",
        "A schedule event is a recovery exactly when its connections run again.",
      );
      return;
    case "instance_test":
      need(hasInstance, "instanceId", "An instance test names its tenant and instance.");
      need(
        facts.testSubject !== undefined && facts.testOutcome !== undefined,
        "testSubject",
        "An instance test names the cell it tests.",
      );
      return;
    case "security":
      need(facts.security !== undefined, "security", "A security event names its category.");
      need(facts.outcome === null, "outcome", "A security event has no run outcome.");
      need(facts.subject === null, "subject", "A security event has no rule subject.");
      // A tenant's name when it is about a tenant; an instance only inside a tenant.
      need(
        facts.instanceId === null || facts.tenantName !== null,
        "tenantName",
        "A security event about an instance names its tenant.",
      );
      return;
    case "offboarding":
      need(facts.offboarding !== undefined, "offboarding", "An offboarding event names its list.");
      need(facts.outcome === null, "outcome", "An offboarding event has no run outcome.");
      need(facts.subject === null, "subject", "An offboarding event has no rule subject.");
      need(facts.instanceId === null, "instanceId", "An offboarding event names no instance.");
      need(facts.tenantName !== null, "tenantName", "An offboarding event names its tenant.");
      return;
    case "canary":
      need(facts.canary !== undefined, "canary", "A canary event names its verdict.");
      need(facts.outcome === null, "outcome", "A canary event has no run outcome.");
      need(facts.subject === null, "subject", "A canary event has no rule subject.");
      need(facts.instanceId === null, "instanceId", "A canary event names no instance.");
      need(facts.tenantName !== null, "tenantName", "A canary event names its tenant.");
      return;
    case "access_invite":
      need(facts.invite !== undefined, "invite", "An invitation names its grant.");
      // The scope's names are in the facts for the words; the event itself has no instance.
      need(facts.instanceId === null, "instanceId", "An invitation carries no instance id.");
      return;
    default:
      need(
        facts.tenantName === null && facts.instanceName === null && facts.instanceId === null,
        "instanceId",
        "A platform test names no tenant and no instance.",
      );
  }
}

/** What only another kind of event may carry. */
function checkOtherKindsFacts(
  facts: z.infer<typeof notificationFactsObject>,
  need: FactRule,
): void {
  if (facts.eventKind !== "access_invite") {
    need(facts.invite === undefined, "invite", "Only an invitation names a grant.");
  }
  if (facts.eventKind !== "credential") {
    need(facts.credential === undefined, "credential", "Only a credential event names one.");
  }
  if (facts.eventKind !== "security") {
    need(facts.security === undefined, "security", "Only a security event names a category.");
  }
  if (facts.eventKind !== "monitor") {
    need(facts.monitor === undefined, "monitor", "Only a skyMonitor event names a monitor.");
  }
  if (facts.eventKind !== "schedule") {
    need(facts.scheduleSkip === undefined, "scheduleSkip", "Only a schedule event names a skip.");
  }
  if (facts.eventKind !== "offboarding") {
    need(facts.offboarding === undefined, "offboarding", "Only an offboarding event names a list.");
  }
  if (facts.eventKind !== "canary") {
    need(facts.canary === undefined, "canary", "Only a canary event names a verdict.");
  }
  if (facts.eventKind !== "run") {
    need(facts.cancelled === undefined, "cancelled", "Only a run event can be cancelled.");
  }
  // #842: only a run the alert covers, a failed or partly failed one, names an open alert.
  need(
    facts.openAlert === undefined ||
      (facts.eventKind === "run" &&
        (facts.outcome === "failure" || facts.partlyFailed !== undefined)),
    "openAlert",
    "Only a failed run names an open skyMonitor alert.",
  );
  // A `schedule` event's recovery has its own rule above: `running_again`, on the warning cell.
  const recoverable = ["run", "monitor", "schedule"].includes(facts.eventKind);
  if (!recoverable) {
    need(!facts.recovered, "recovered", "Only a run or a skyMonitor event can be a recovery.");
  } else if (facts.eventKind !== "schedule" && facts.outcome !== "success") {
    // Recovered is a success notification (contract §3, round-2 critic ruling 8).
    need(!facts.recovered, "recovered", "Only a successful run can be a recovery.");
  }
  if (facts.eventKind !== "instance_test") {
    need(
      facts.testSubject === undefined && facts.testOutcome === undefined,
      "testSubject",
      "Only an instance test names a tested cell.",
    );
  }
}

/** The facts of one notification event: `notificationFactsObject` under its per-kind rules. */
export const notificationFactsSchema = notificationFactsObject.superRefine(checkNotificationFacts);
export type NotificationFacts = z.infer<typeof notificationFactsSchema>;

// ---------------------------------------------------------------------------------------------
// Views and bodies (§6.2)
// ---------------------------------------------------------------------------------------------

const ruleEmailSchema = z.email().max(320);

/** One (subject, outcome) cell's rule, as the rules PUT carries it. */
export const notificationRuleSchema = z
  .object({
    subject: z.enum(NOTIFY_SUBJECTS),
    outcome: z.enum(NOTIFY_OUTCOMES),
    enabled: z.boolean(),
    /** Lowercased, trimmed and deduplicated server-side. */
    emails: z.array(ruleEmailSchema).max(MAX_RULE_EMAILS),
    teams: z.boolean(),
    // No L1 switch (round 3): the L1 team hears every failure once the deployment has recipients.
  })
  .strict();
export type NotificationRule = z.infer<typeof notificationRuleSchema>;

/** Exactly one rule per cell: with `length(9)`, nine distinct cells. */
export function coversEveryCell(
  rules: ReadonlyArray<{ subject: string; outcome: string }>,
): boolean {
  return new Set(rules.map((r) => `${r.subject}.${r.outcome}`)).size === RULE_CELLS.length;
}

export const notificationRulesViewSchema = z.object({
  instanceId: z.uuid(),
  /** False: the screens say "No notification rule yet". */
  hasRules: z.boolean(),
  /** Always nine, in `RULE_CELLS` order; a cell with no row is shown `enabled: false`. */
  rules: z
    .array(
      notificationRuleSchema.extend({
        /** Masked unless the caller holds `instance.settings.write` (`emailsMasked`). */
        emails: z.array(z.string()),
      }),
    )
    .length(RULE_CELLS.length),
  repeatWindowMinutes: z.number().int().min(REPEAT_WINDOW.min).max(REPEAT_WINDOW.max),
  /** max(updated_at) over the rules and the prefs row, ISO with milliseconds; null when none. */
  version: z.string().nullable(),
  /**
   * True when the instance has a Teams webhook stored. Neither the URL nor its vault and secret
   * names: those are internals the customer's browser has no use for (SEC-17, lead addendum 2
   * of 2026-09-30). The General tab's webhook field reads the settings view.
   */
  teamsConfigured: z.boolean(),
  /** The status row exists and the key and the sender are set. */
  emailConfigured: z.boolean(),
  emailsMasked: z.boolean(),
  /** #1111: the tenant's Allowed notification domains and who has access, to mark and refuse. */
  recipientPolicy: recipientPolicySchema,
});
export type NotificationRulesView = z.infer<typeof notificationRulesViewSchema>;

/** The rules PUT. It never carries the webhook (round-1 ruling 14). */
export const putNotificationRulesBodySchema = z
  .object({
    rules: z
      .array(notificationRuleSchema)
      .length(RULE_CELLS.length)
      .refine(coversEveryCell, "one rule per subject and outcome"),
    repeatWindowMinutes: z.number().int().min(REPEAT_WINDOW.min).max(REPEAT_WINDOW.max),
    /** The view's `version`; a mismatch is 409 STALE_UPDATE. Null on an instance's first save. */
    expectedVersion: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();
export type PutNotificationRulesBody = z.infer<typeof putNotificationRulesBodySchema>;

/** "Send a test notification": one saved cell, to its own targets only (never L1). */
export const instanceTestBodySchema = z
  .object({ subject: z.enum(NOTIFY_SUBJECTS), outcome: z.enum(NOTIFY_OUTCOMES) })
  .strict();
export type InstanceTestBody = z.infer<typeof instanceTestBodySchema>;

/** "Send a test email": always to the caller's own account email, so the body is empty. */
export const emailTestBodySchema = z.object({}).strict();
export type EmailTestBody = z.infer<typeof emailTestBodySchema>;

export const testAcceptedSchema = z.object({ eventId: z.uuid() });
export type TestAccepted = z.infer<typeof testAcceptedSchema>;

export const testEventParamsSchema = z.object({ eventId: z.uuid() });
export type TestEventParams = z.infer<typeof testEventParamsSchema>;

export const testStatusViewSchema = z.object({
  eventId: z.uuid(),
  status: z.enum(TEST_STATUSES),
  /** `deriveTestStatus`'s code: set only for `failed`. */
  failureCode: z.enum(DELIVERY_FAILURE_CODES).nullable(),
  deliveries: z.array(
    z.object({
      channel: z.enum(NOTIFY_CHANNELS),
      /** A masked address, or "Teams channel". */
      target: z.string(),
      status: z.enum(DELIVERY_STATUSES),
      attempt: z.number().int(),
      failureCode: z.enum(DELIVERY_FAILURE_CODES).nullable(),
      sentAt: z.string().nullable(),
    }),
  ),
});
export type TestStatusView = z.infer<typeof testStatusViewSchema>;

/** Why a delivery exists: a (subject, outcome) cell, a rule for chosen targets, or a Watch (alert routing). */
export const NOTIFY_ROUTES = ["rule", "scoped_rule", "watch"] as const;
export type NotifyRoute = (typeof NOTIFY_ROUTES)[number];

/** `GET …/notifications/deliveries?limit=20`: the newest rows, every status. */
export const DELIVERIES_LIMIT_MAX = 20;
export const deliveriesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(DELIVERIES_LIMIT_MAX).default(DELIVERIES_LIMIT_MAX),
});
export type DeliveriesQuery = z.infer<typeof deliveriesQuerySchema>;

export const deliveryRowSchema = z.object({
  id: z.uuid(),
  eventId: z.uuid(),
  createdAt: z.string(),
  eventKind: z.enum(NOTIFY_EVENT_KINDS),
  runId: z.string().nullable(),
  /** From the facts; null for the email test. */
  subject: z.enum(NOTIFY_SUBJECTS).nullable(),
  outcome: z.enum(NOTIFY_OUTCOMES).nullable(),
  recovered: z.boolean(),
  /** #1422: the run was cancelled (its facts carry `cancelled` and no outcome). */
  cancelled: z.boolean().optional(),
  channel: z.enum(NOTIFY_CHANNELS),
  /** A masked address, "Teams channel" or "L1 team": never an address in the clear. */
  target: z.object({ kind: z.enum(NOTIFY_TARGET_KINDS), label: z.string() }),
  status: z.enum(DELIVERY_STATUSES),
  eventStatus: z.enum(EVENT_STATUSES),
  /** Set on held rows, for "Held back: the same failure within {n} minutes". */
  repeatWindowMinutes: z.number().int().nullable(),
  attempt: z.number().int(),
  nextAttemptAt: z.string().nullable(),
  failureCode: z.enum(DELIVERY_FAILURE_CODES).nullable(),
  httpStatus: z.number().int().nullable(),
  sentAt: z.string().nullable(),
  /**
   * Alert routing: why the row exists, and the rule's name for a scoped rule. Optional so a
   * reader that predates routing still parses; the server always sends both.
   */
  route: z.enum(NOTIFY_ROUTES).optional(),
  routeRuleName: z.string().nullable().optional(),
});
export type DeliveryRow = z.infer<typeof deliveryRowSchema>;

/**
 * Round 4 item 3, "Runs with no alert": `GET …/notifications/quiet-runs?limit=20`, the newest run
 * events of the instance that made no delivery row, so "why no email" answers itself.
 * `no_rule`: no rule cell, rule for chosen targets or Watch covered the run's outcome;
 * `no_recipients`: one did, but it names no one to send to. Fact names and closed words only
 * (SEC-17), never an address.
 */
export const QUIET_RUNS_LIMIT_MAX = 20;
export const QUIET_RUN_REASONS = ["no_rule", "no_recipients"] as const;

export const quietRunsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(QUIET_RUNS_LIMIT_MAX).default(QUIET_RUNS_LIMIT_MAX),
});

export const quietRunRowSchema = z.object({
  eventId: z.uuid(),
  runId: z.string().nullable(),
  createdAt: z.string(),
  subject: z.enum(NOTIFY_SUBJECTS).nullable(),
  outcome: z.enum(NOTIFY_OUTCOMES).nullable(),
  recovered: z.boolean(),
  /** #1422: the run was cancelled, which only Every result hears about. */
  cancelled: z.boolean().optional(),
  reason: z.enum(QUIET_RUN_REASONS),
  scheduleName: z.string().nullable(),
  connectionNames: z.array(z.string()),
  connectionsMore: z.number().int(),
});
export type QuietRunRow = z.infer<typeof quietRunRowSchema>;

/** The Email delivery card: the runner-written status row and 24-hour counts, never the key. */
export const emailDeliveryViewSchema = z.object({
  /** False: no status row yet ("The runner has not reported email status yet."). */
  reported: z.boolean(),
  vaultName: z.string().nullable(),
  secretName: z.string().nullable(),
  keyStatus: z.enum(EMAIL_KEY_STATUSES).nullable(),
  sender: z.string().nullable(),
  checkedAt: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
  lastFailureCode: z.enum(DELIVERY_FAILURE_CODES).nullable(),
  /**
   * Deliveries created in the last 24 hours; `held` and `skipped` are not counted. `parked`
   * (#463): emails kept, not retried, until a platform operator releases them.
   */
  last24h: z.object({
    sent: z.number().int(),
    failed: z.number().int(),
    waiting: z.number().int(),
    parked: z.number().int(),
  }),
  /** The caller's own address, masked, for the test dialog; null answers 409 on send. */
  callerEmailMasked: z.string().nullable(),
});
export type EmailDeliveryView = z.infer<typeof emailDeliveryViewSchema>;

export const l1ViewSchema = z.object({
  emails: z.array(z.string()),
  updatedAt: z.string().nullable(),
  /** Security alerts: the categories the L1 team also receives (none until switched on). */
  securityCategories: z.array(z.enum(SECURITY_CATEGORIES)),
});
export type L1View = z.infer<typeof l1ViewSchema>;

export const putL1BodySchema = z
  .object({
    emails: z.array(ruleEmailSchema).max(MAX_RULE_EMAILS),
    /** The view's `updatedAt`; compared at millisecond precision, a mismatch is 409. */
    expectedUpdatedAt: z.iso.datetime({ offset: true }).nullable(),
    /** Security alerts for the L1 team. Absent keeps what is stored (an older client's save). */
    securityCategories: z
      .array(z.enum(SECURITY_CATEGORIES))
      .max(SECURITY_CATEGORIES.length)
      .refine((list) => new Set(list).size === list.length, "Each category once.")
      .optional(),
  })
  .strict();
export type PutL1Body = z.infer<typeof putL1BodySchema>;

/**
 * `GET /platform/notifications/security/deliveries` (security alerts, owner answer Q6: shown to
 * PlatformAdmins only in v1): the newest security events, one row per delivery, and one row with
 * no target for an event nobody was subscribed to, so the gap shows.
 */
export const SECURITY_DELIVERIES_LIMIT = 50;

export const securityDeliveryRowSchema = z.object({
  /** The delivery's id, or the event's for an event with no delivery. */
  id: z.uuid(),
  eventId: z.uuid(),
  createdAt: z.string(),
  category: z.enum(SECURITY_CATEGORIES),
  tenantName: z.string().nullable(),
  instanceName: z.string().nullable(),
  /** Null for an event with no delivery (`eventStatus` says why). */
  channel: z.enum(NOTIFY_CHANNELS).nullable(),
  /** A masked address, "Teams channel" or "L1 team"; null for an event with no delivery. */
  target: z.object({ kind: z.enum(NOTIFY_TARGET_KINDS), label: z.string() }).nullable(),
  status: z.enum(DELIVERY_STATUSES).nullable(),
  eventStatus: z.enum(EVENT_STATUSES),
  failureCode: z.enum(DELIVERY_FAILURE_CODES).nullable(),
  sentAt: z.string().nullable(),
});
export type SecurityDeliveryRow = z.infer<typeof securityDeliveryRowSchema>;

// ---------------------------------------------------------------------------------------------
// Alert routing (docs/research/2026-09-28-design-alert-routing.md, product gaps row 25): a rule
// for chosen connections or schedules, and a personal Watch. Both ADD recipients to the cells
// above; neither ever removes anyone a cell reaches (owner, 2026-09-28).
// ---------------------------------------------------------------------------------------------

/** What a scoped rule or a Watch points at. Transformations are a later phase (owner Q5). */
export const WATCH_TARGET_TYPES = ["dataflow", "schedule"] as const;
export type WatchTargetType = (typeof WATCH_TARGET_TYPES)[number];

/** The chosen or watched target's own result, for the "You watch" line; `recovered` is "works again". */
export const FOCUS_OUTCOMES = ["failure", "warning", "success", "recovered"] as const;
export type FocusOutcome = (typeof FOCUS_OUTCOMES)[number];

/** Per instance, and per rule. The migration's CHECK holds the second. */
export const MAX_SCOPED_RULES = 50;
export const MAX_SCOPED_RULE_TARGETS = 50;
export const SCOPED_RULE_NAME_MAX = 80;
/** Connections an event names with their own outcome; failures are kept first when cut. */
export const DATAFLOW_OUTCOMES_MAX = 200;

const outcomeList = z
  .array(z.enum(NOTIFY_OUTCOMES))
  .min(1)
  .max(NOTIFY_OUTCOMES.length)
  .refine((list) => new Set(list).size === list.length, "Each outcome once.");

/**
 * One connection of a run with its own result (`connectionOutcomesOf` in the runner): its id, its
 * name (a name is allowed in facts, SEC-17), a closed outcome, and whether it works again after
 * its previous run failed. Never an error text, a table name or a count of rows.
 */
export const dataflowOutcomeSchema = z
  .object({
    id: z.uuid(),
    name: factName,
    outcome: z.enum(NOTIFY_OUTCOMES),
    recovered: z.boolean(),
  })
  .strict()
  .refine((entry) => !entry.recovered || entry.outcome === "success", {
    message: "Only a success can be a recovery.",
    path: ["recovered"],
  });
export type DataflowOutcome = z.infer<typeof dataflowOutcomeSchema>;

/** The event's scope columns (`schedule_id`, `dataflow_outcomes`); null on older events. */
export const eventScopeSchema = z
  .object({
    scheduleId: z.uuid().nullable(),
    dataflowOutcomes: z.array(dataflowOutcomeSchema).max(DATAFLOW_OUTCOMES_MAX).nullable(),
  })
  .strict();
export type EventScope = z.infer<typeof eventScopeSchema>;

/**
 * One rule for chosen connections or schedules, as the scoped-rules PUT carries it. `id` keeps a
 * saved rule's identity (Recent deliveries names the rule by it); a rule without one is new. No
 * L1 switch: a scoped rule adds no L1 target of its own (the L1 team hears every failure anyway,
 * round 3). An empty target list is allowed only on an
 * off rule (a rule whose last connection was deleted stays, switched off).
 */
export const scopedRuleSchema = z
  .object({
    id: z.uuid().optional(),
    name: z.string().trim().min(1).max(SCOPED_RULE_NAME_MAX),
    targetType: z.enum(WATCH_TARGET_TYPES),
    targetIds: z
      .array(z.uuid())
      .max(MAX_SCOPED_RULE_TARGETS)
      .refine((ids) => new Set(ids).size === ids.length, "Each connection or schedule once."),
    outcomes: outcomeList,
    enabled: z.boolean(),
    /** Lowercased, trimmed and deduplicated server-side. */
    emails: z.array(ruleEmailSchema).max(MAX_RULE_EMAILS),
    teams: z.boolean(),
  })
  .strict()
  .refine((rule) => !rule.enabled || rule.targetIds.length > 0, {
    message: "Choose at least one connection or schedule, or turn the rule off.",
    path: ["targetIds"],
  });
export type ScopedRule = z.infer<typeof scopedRuleSchema>;

/** The scoped-rules PUT: a full replace, guarded like the rules PUT by `expectedVersion`. */
export const putScopedRulesBodySchema = z
  .object({
    rules: z
      .array(scopedRuleSchema)
      .max(MAX_SCOPED_RULES)
      .refine(
        (rules) =>
          new Set(rules.map((rule) => rule.name.trim().toLowerCase())).size === rules.length,
        "Each rule needs its own name.",
      ),
    /** The view's `version`; a mismatch is 409 STALE_UPDATE. Null while the instance has none. */
    expectedVersion: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();
export type PutScopedRulesBody = z.infer<typeof putScopedRulesBodySchema>;

export const SCOPED_RULES_STALE_MESSAGE =
  "Someone else changed the rules for chosen connections. Reload to see their version.";

/** `GET /instances/current/notifications/scoped-rules`. */
export const scopedRulesViewSchema = z.object({
  instanceId: z.uuid(),
  rules: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      targetType: z.enum(WATCH_TARGET_TYPES),
      /** Each target with its current name; null when it no longer exists ("Deleted connection"). */
      targets: z.array(z.object({ id: z.uuid(), name: z.string().nullable() })),
      outcomes: z.array(z.enum(NOTIFY_OUTCOMES)),
      enabled: z.boolean(),
      /** Masked unless the caller holds `instance.settings.write` (`emailsMasked`). */
      emails: z.array(z.string()),
      teams: z.boolean(),
    }),
  ),
  /** max(updated_at) over the instance's scoped rules (every save rewrites all); null when none. */
  version: z.string().nullable(),
  emailsMasked: z.boolean(),
  /** #1111: as on the rules view. */
  recipientPolicy: recipientPolicySchema,
});
export type ScopedRulesView = z.infer<typeof scopedRulesViewSchema>;

/** A Watch's default (owner, 2026-09-28): failures and warnings; "works again" always. */
export const WATCH_DEFAULT_OUTCOMES: readonly NotifyOutcome[] = ["failure", "warning"];
/** "Every result": success mails are opt-in. */
export const WATCH_EVERY_OUTCOMES: readonly NotifyOutcome[] = ["failure", "warning", "success"];

/** `PUT /dataflows/:id/watch` and `PUT /schedules/:id/watch`. No user id: only your own Watch. */
export const watchBodySchema = z
  .object({ outcomes: outcomeList.default([...WATCH_DEFAULT_OUTCOMES]) })
  .strict();

export type WatchPreset = "problems" | "every" | "custom";

/** The button's two choices, "Failures and warnings" and "Every result". */
export function watchPresetOf(outcomes: readonly NotifyOutcome[]): WatchPreset {
  const set = new Set(outcomes);
  if (set.size === 2 && set.has("failure") && set.has("warning")) return "problems";
  if (set.size === 3) return "every";
  return "custom";
}

export const watchViewSchema = z.object({
  targetType: z.enum(WATCH_TARGET_TYPES),
  targetId: z.uuid(),
  /** The connection's or schedule's current name; null when it no longer exists. */
  name: z.string().nullable(),
  outcomes: z.array(z.enum(NOTIFY_OUTCOMES)),
  createdAt: z.string(),
});
export type WatchView = z.infer<typeof watchViewSchema>;

/** `GET /instances/current/watches`: the caller's own watches here, and where they are sent. */
export const myWatchesViewSchema = z.object({
  watches: z.array(watchViewSchema),
  /** The caller's account address, masked; null when the account has none (the button says so). */
  callerEmailMasked: z.string().nullable(),
});
export type MyWatchesView = z.infer<typeof myWatchesViewSchema>;

/** What `PUT` and `DELETE …/:id/watch` answer: the caller's Watch on that one target now. */
export const watchStateSchema = z.object({
  targetType: z.enum(WATCH_TARGET_TYPES),
  targetId: z.uuid(),
  watching: z.boolean(),
  /** Empty when not watching. */
  outcomes: z.array(z.enum(NOTIFY_OUTCOMES)),
});
export type WatchState = z.infer<typeof watchStateSchema>;

/** Why the Watch button is disabled for an account with no address (the server's 409 too). */
export const WATCH_NO_EMAIL_MESSAGE =
  "Your account has no email address, so there is nowhere to send a Watch's alerts.";

/** Recent deliveries' "Why" cell. */
export function routeLabel(
  route: NotifyRoute | null | undefined,
  ruleName: string | null | undefined,
): string {
  if (route === "watch") return "Watch";
  if (route === "scoped_rule") return ruleName ? `Rule: ${ruleName}` : "Rule for chosen targets";
  return "Rule";
}
